import {
  CardGlass,
  ErrorPage,
  BlobCtaButton,
  LoadingSpinner,
  SelectField,
  ThemeBackground,
  Typography,
  type SelectFieldOption,
} from "@cupcodev/ui";
import { useState, type ReactNode } from "react";
import type { AccountsExperienceState } from "../../application/auth/accounts-experience";
import { useI18n } from "../i18n/I18nProvider";
import { useAccountsExperience } from "./AccountsExperienceProvider";

export const AccountsExperiencePage = ({ state }: { state: Exclude<AccountsExperienceState, Readonly<{ kind: "AUTHENTICATED" }>> }) => {
  const { t } = useI18n();
  const { notice, startAuthentication, selectOrganization } = useAccountsExperience();

  if (state.kind === "ORGANIZATION_SELECTION_REQUIRED") {
    return (
      <StandaloneSurface>
        <OrganizationSelectionSurface
          state={state}
          onSelect={selectOrganization}
        />
      </StandaloneSurface>
    );
  }

  if (state.kind === "CALLBACK_PENDING") {
    return (
      <StandaloneSurface>
        <main className="accounts-experience-main" aria-busy="true">
          <CardGlass variant="glass-md" elevation={2} className="accounts-experience-card">
            <div className="accounts-callback-status" role="status" aria-live="polite">
              <LoadingSpinner label={t("account.callback.loading")} />
              <Typography as="h1" variant="title-lg">{t("account.callback.title")}</Typography>
              <Typography as="p" variant="body-md" tone="muted">{t("account.callback.body")}</Typography>
            </div>
          </CardGlass>
        </main>
      </StandaloneSurface>
    );
  }

  const presentation = presentationForState(state);
  return (
    <StandaloneSurface>
      <main className="accounts-experience-main">
        <ErrorPage
          code={presentation.code}
          title={t(presentation.titleKey)}
          description={t(presentation.bodyKey)}
          actionLabel={t(presentation.actionKey ?? "common.back")}
          {...(presentation.actionKey === undefined
            ? {}
            : {
                actionLabel: t(presentation.actionKey),
                onAction: startAuthentication,
              })}
        />
        {notice === "AUTH_INITIATION_NOT_CONNECTED" ? (
          <p className="accounts-experience-notice" role="status">{t("account.authAction.notConnected")}</p>
        ) : null}
      </main>
    </StandaloneSurface>
  );
};

const OrganizationSelectionSurface = ({
  state,
  onSelect,
}: {
  state: Extract<AccountsExperienceState, Readonly<{ kind: "ORGANIZATION_SELECTION_REQUIRED" }>>;
  onSelect(organizationId: Extract<AccountsExperienceState, Readonly<{ kind: "ORGANIZATION_SELECTION_REQUIRED" }>>["organizations"][number]["organizationId"]): void;
}) => {
  const { t } = useI18n();
  const initial = state.organizations[0]?.organizationId.value ?? "";
  const [selected, setSelected] = useState(initial);
  const options = state.organizations.map<SelectFieldOption<string>>((organization) => ({
    value: organization.organizationId.value,
    label: organization.displayName,
  }));
  const selectedOrganization = state.organizations.find(
    (organization) => organization.organizationId.value === selected,
  );

  return (
    <main className="accounts-experience-main">
      <CardGlass variant="glass-md" elevation={2} className="accounts-experience-card">
        <div className="accounts-experience-stack">
          <Typography as="h1" variant="display-sm">{t("organization.selection.title")}</Typography>
          <Typography as="p" variant="body-md" tone="muted">{t("organization.selection.description")}</Typography>
          <SelectField
            value={selected}
            onChange={setSelected}
            options={options}
            ariaLabel={t("organization.selection.label")}
            placeholder={t("organization.selection.placeholder")}
            triggerClassName="accounts-experience-select"
          />
          <BlobCtaButton
            type="button"
            disabled={selectedOrganization === undefined}
            onClick={() => {
              if (selectedOrganization !== undefined) onSelect(selectedOrganization.organizationId);
            }}
          >
            {t("organization.selection.continue")}
          </BlobCtaButton>
        </div>
      </CardGlass>
    </main>
  );
};

const StandaloneSurface = ({ children }: { children?: ReactNode }) => (
  <div className="accounts-experience-root">
    <ThemeBackground enableStars />
    {children}
  </div>
);

type Presentation = Readonly<{
  code: string;
  titleKey: string;
  bodyKey: string;
  actionKey?: string;
}>;

const presentationForState = (
  state: Exclude<AccountsExperienceState, Readonly<{ kind: "AUTHENTICATED" | "ORGANIZATION_SELECTION_REQUIRED" | "CALLBACK_PENDING" }>>,
): Presentation => {
  switch (state.kind) {
    case "AUTH_REQUIRED":
      return {
        code: "401",
        titleKey: state.reason === "LOGIN_CANCELLED" ? "account.loginCancelled.title" : "account.authRequired.title",
        bodyKey: state.reason === "LOCAL_LOGOUT" ? "account.authRequired.afterLogoutBody" : state.reason === "LOGIN_CANCELLED" ? "account.loginCancelled.body" : "account.authRequired.body",
        actionKey: "account.actions.signIn",
      };
    case "SESSION_EXPIRED":
      return { code: "401", titleKey: "account.sessionExpired.title", bodyKey: "account.sessionExpired.body", actionKey: "account.actions.signInAgain" };
    case "ACCOUNTS_UNAVAILABLE":
      return { code: "503", titleKey: "errors.accountsUnavailable.title", bodyKey: "errors.accountsUnavailable.body" };
    case "MEMBERSHIP_REQUIRED":
      return { code: "403", titleKey: "errors.membershipRequired.title", bodyKey: "errors.membershipRequired.body" };
    case "ORGANIZATION_UNAVAILABLE":
      return { code: "404", titleKey: "organization.unavailable.title", bodyKey: "organization.unavailable.body" };
    case "FORBIDDEN":
      return { code: "403", titleKey: "errors.forbidden.title", bodyKey: "errors.forbidden.body" };
    case "NOT_FOUND":
      return { code: "404", titleKey: "errors.notFound.title", bodyKey: "errors.notFound.body" };
    case "RESOURCE_STATE_CONFLICT":
      return { code: "409", titleKey: "errors.resourceConflict.title", bodyKey: "errors.resourceConflict.body" };
    case "INVALID_ENTRY_CONTEXT":
      return { code: "400", titleKey: "errors.invalidEntryContext.title", bodyKey: "errors.invalidEntryContext.body", actionKey: "account.actions.restart" };
    case "EXPIRED_ENTRY_CONTEXT":
      return { code: "400", titleKey: "errors.expiredEntryContext.title", bodyKey: "errors.expiredEntryContext.body", actionKey: "account.actions.restart" };
    default:
      return assertNever(state);
  }
};

const assertNever = (value: never): never => {
  throw new Error(`Unhandled Accounts experience page state: ${String(value)}`);
};
